import React, { useEffect, useRef } from 'lib/react';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import {
  EVGrid, EVPaper, EVTypography, EVAlert, EVButton,
} from 'lib/components';
import { scrollToTop } from 'utils/app.utils';
import { LOAD_CONTACT_INFO } from 'constants/ActionTypes';
import { DASHBOARD_ROUTE } from 'constants/RouteConstants';
import RetryScreen from 'commonComponents/retry-screen';
import history from 'store/CreateHistory';
import Banner from 'commonComponents/banner';
import ContactInformationStyles from './ContactInformationStyles';
import ContactAddressInformation from './components/contactAddressInformation';
import CompanyLogo from './components/companyLogo';

import {
  submitContactInfo,
  updateContactAddressInfo,
  submitContactInfoValidationError,
  resetContactInfoValidationError,
} from './ContactInformation.actions';
import { validationandRegexErrors } from './ContactInformation.util';

function ContactInformation() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const classes = ContactInformationStyles();
  const inputRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    company: useRef(null),
    emailAddress: useRef(null),
    emailCC: useRef(null),
    country: useRef(null),
    phone: useRef(null),
    address1: useRef(null),
    city: useRef(null),
    state: useRef(null),
    zip: useRef(null),
  };

  const {
    loading,
    addressInfo,
    addressInfoErrors,
    islogoLoadError,
    companyLogo,
    submitAddressInfo,
    isInError,
  } = useSelector((
    { userData, contactInformation },
  ) => ({ ...contactInformation, isInError: userData.error?.companyInformation }));

  useEffect(() => {
    dispatch({ type: LOAD_CONTACT_INFO });
  }, []);

  if (isInError || islogoLoadError) {
    return <RetryScreen handleRetryButtonClick={() => dispatch({ type: LOAD_CONTACT_INFO })} />;
  }

  const handleAddressInfoChange = (fieldId, value) => {
    dispatch(updateContactAddressInfo(fieldId, value));
  };

  const showValidationBanner = Object.values(addressInfoErrors).some((val) => val !== '');

  const handleContactInfoSubmission = (event) => {
    event.preventDefault();
    scrollToTop();
    dispatch(resetContactInfoValidationError());
    const validationErrors = validationandRegexErrors(t, addressInfo);
    if (Object.keys(validationErrors).length > 0 && Object.values(validationErrors).some((val) => val !== '')) {
      dispatch(submitContactInfoValidationError(validationErrors));
    } else {
      dispatch(submitContactInfo());
    }
  };
  const submitAddressState = {
    ...submitAddressInfo,
    show: submitAddressInfo.showMessage && loading === false,
    severity: submitAddressInfo.errorMessage ? 'error' : 'success',
    message: submitAddressInfo.errorMessage ? t('contact_information__submit_fail_message') : t('contact_information__submit_success_message'),
  };

  const validationMessageBanner = () => (
    <EVAlert severity="error" className={classes.alert}>
      <EVGrid container className={classes.mainContainer} direction="column">
        <EVTypography className={classes.title}>{t('validation_message_banner__title')}</EVTypography>
        <EVTypography className={classes.errorHeading}>{t('validation_message_banner__error_heading')}</EVTypography>
        <EVGrid container item direction="column" className={classes.errorContainer}>
          <ul>
            {Object.entries(addressInfoErrors).filter(([, message]) => message.length > 0).map(([key, message]) => (
              <li key={`validation-for-${key}`}>
                <EVButton
                  type="button"
                  id={`validation-for-${key}`}
                  onClick={() => {
                    // #genai - optional chaining, same truthiness guard as before
                    if (inputRefs[key]?.current) {
                      inputRefs[key].current.focus();
                    }
                  }}
                  className={classes.button}
                  disableRipple
                >
                  <EVTypography className={classes.message}>{message}</EVTypography>
                </EVButton>
              </li>
            ))}
          </ul>
        </EVGrid>
      </EVGrid>
    </EVAlert>
  );

  return (
    <form onSubmit={handleContactInfoSubmission}>
      {!loading && Object.keys(addressInfoErrors).length > 0 && showValidationBanner && (validationMessageBanner())}
      {submitAddressState.show && (
        <Banner
          severity={submitAddressState.severity}
          message={submitAddressState.message}
        />
      )}
      <EVGrid container className={classes.contactInfocontainer}>
        <EVGrid container className={classes.topContainer}>
          <EVPaper className={classes.paper}>
            <ContactAddressInformation
              isLoading={loading}
              addressInfo={addressInfo}
              addressInfoErrors={addressInfoErrors}
              handleChange={handleAddressInfoChange}
              onCancel={() => history.replace(DASHBOARD_ROUTE)}
              inputRefs={inputRefs}
            />
          </EVPaper>
        </EVGrid>
        <EVPaper className={classes.logopaper}>
          <EVGrid className={classes.logoDetail}>
            <CompanyLogo companyLogo={companyLogo} isLoading={loading} />
          </EVGrid>
        </EVPaper>
      </EVGrid>
    </form>
  );
}

export default ContactInformation;
